import { Badge, Card, CardContent, CardDescription, CardHeader, CardTitle } from '@auctisync/ui';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { SignOutButton } from '../../components/SignOutButton';
import { readClaims, useSession } from '../../hooks/useSession';
import { fetchWithAuth } from '../../lib/accessToken';

/**
 * Super-admin briefs view — Feature 6.
 *
 * Shows the latest persisted daily diagnostics report + weekly Sonnet
 * synthesis brief side by side. Both are generated on cron + delivered
 * to Telegram via Comms Relay; this dashboard is the historical/
 * web-friendly view of the same content.
 */

interface DailyReport {
  productId: string;
  reportDate: string;
  markdownBody: string;
  generatedAt: string;
  deliveredAt: string | null;
}

interface WeeklyBrief {
  productId: string;
  briefDate: string;
  modelName: string;
  promptTemplateName: string;
  promptTemplateVersion: number;
  markdownBody: string;
  inputTokens: number;
  outputTokens: number;
  generatedAt: string;
  deliveredAt: string | null;
}

interface BriefsSnapshot {
  asOfMs: number;
  daily: DailyReport | null;
  weekly: WeeklyBrief | null;
}

interface ApiError {
  error: string;
  detail: string;
}

type FetchState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ok'; data: BriefsSnapshot }
  | { kind: 'error'; status: number; message: string };

function ieBaseUrl(): string | null {
  const url = import.meta.env.VITE_INTELLIGENCE_BASE_URL as string | undefined;
  return url && url.length > 0 ? url.replace(/\/$/, '') : null;
}

function formatDeliveryStatus(deliveredAt: string | null): string {
  if (deliveredAt === null) return 'pending delivery';
  const dt = new Date(deliveredAt);
  return `delivered ${dt.toUTCString()}`;
}

export function SuperBriefs() {
  const { session, userId } = useSession();
  const { appRole } = readClaims(session);
  const [state, setState] = useState<FetchState>({ kind: 'idle' });
  const [refreshNonce, setRefreshNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const base = ieBaseUrl();
    if (!base) {
      setState({
        kind: 'error',
        status: 0,
        message: 'VITE_INTELLIGENCE_BASE_URL is not configured.',
      });
      return;
    }
    if (userId === null) {
      setState({ kind: 'error', status: 0, message: 'No active session.' });
      return;
    }
    setState({ kind: 'loading' });
    fetchWithAuth(`${base}/v1/dashboard/briefs/latest`)
      .then(async (res) => {
        if (cancelled) return;
        if (!res.ok) {
          const body = (await res.json().catch(() => null)) as ApiError | null;
          setState({
            kind: 'error',
            status: res.status,
            message: body?.detail ?? `HTTP ${res.status}`,
          });
          return;
        }
        const data = (await res.json()) as BriefsSnapshot;
        setState({ kind: 'ok', data });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'unknown error';
        setState({ kind: 'error', status: 0, message });
      });
    return () => {
      cancelled = true;
    };
  }, [refreshNonce, userId]);

  return (
    <main style={{ padding: '2rem', maxWidth: '76rem', margin: '0 auto' }}>
      <div
        style={{
          marginBottom: '1.5rem',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: '1rem',
        }}
      >
        <div>
          <h1
            style={{
              fontFamily: 'var(--font-heading-active)',
              fontSize: '1.875rem',
              fontWeight: 700,
              color: 'var(--text)',
              marginBottom: '0.25rem',
            }}
          >
            Briefs
          </h1>
          <p style={{ color: 'var(--text-muted)' }}>
            Signed in as {session?.user.email ?? 'unknown'} ·{' '}
            <Badge variant="outline">{appRole ?? 'no role'}</Badge> ·{' '}
            <Link to="/super/dashboard" style={{ color: 'var(--brand-primary)' }}>
              ← Super dashboard
            </Link>
          </p>
        </div>
        <button
          onClick={() => setRefreshNonce((n) => n + 1)}
          type="button"
          disabled={state.kind === 'loading'}
          style={{
            padding: '0.4rem 0.9rem',
            borderRadius: '0.4rem',
            border: '1px solid var(--border)',
            background: 'var(--surface-elev)',
            color: 'var(--text)',
            fontWeight: 600,
            fontSize: '0.85rem',
            cursor: state.kind === 'loading' ? 'wait' : 'pointer',
          }}
        >
          {state.kind === 'loading' ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      <SignOutButton redirectTo="/super" />

      <div style={{ marginTop: '1.5rem', display: 'grid', gap: '1.5rem' }}>
        {state.kind === 'idle' || state.kind === 'loading' ? (
          <Card>
            <CardContent>
              <p style={{ color: 'var(--text-muted)' }}>
                {state.kind === 'idle' ? 'Initializing…' : 'Loading briefs…'}
              </p>
            </CardContent>
          </Card>
        ) : state.kind === 'error' ? (
          <Card>
            <CardHeader>
              <CardTitle>Briefs unavailable</CardTitle>
              <CardDescription>HTTP {state.status || 'no-network'}</CardDescription>
            </CardHeader>
            <CardContent>
              <pre
                style={{
                  whiteSpace: 'pre-wrap',
                  color: 'var(--semantic-danger)',
                  fontFamily: 'var(--font-mono, ui-monospace)',
                  fontSize: '0.85rem',
                  margin: 0,
                }}
              >
                {state.message}
              </pre>
            </CardContent>
          </Card>
        ) : (
          <BriefsView data={state.data} />
        )}
      </div>
    </main>
  );
}

function BriefsView({ data }: { data: BriefsSnapshot }) {
  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Latest daily diagnostics</CardTitle>
          <CardDescription>
            {data.daily === null
              ? 'No daily report yet (next tick at 13:00 UTC = 8am CT).'
              : `Report date ${data.daily.reportDate} · ${formatDeliveryStatus(data.daily.deliveredAt)}`}
          </CardDescription>
        </CardHeader>
        {data.daily !== null && <MarkdownBody body={data.daily.markdownBody} />}
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Latest weekly synthesis brief</CardTitle>
          <CardDescription>
            {data.weekly === null
              ? 'No weekly brief yet (next tick Monday 13:00 UTC = 8am CT).'
              : `Brief date ${data.weekly.briefDate} · ${formatDeliveryStatus(data.weekly.deliveredAt)}`}
          </CardDescription>
        </CardHeader>
        {data.weekly !== null && (
          <>
            <MarkdownBody body={data.weekly.markdownBody} />
            <CardContent>
              <p style={{ fontSize: '0.7rem', color: 'var(--text-muted)', margin: 0 }}>
                Generated by {data.weekly.modelName} · template {data.weekly.promptTemplateName} v
                {data.weekly.promptTemplateVersion} ·{' '}
                {data.weekly.inputTokens + data.weekly.outputTokens} tokens
              </p>
            </CardContent>
          </>
        )}
      </Card>
    </>
  );
}

function MarkdownBody({ body }: { body: string }) {
  return (
    <CardContent>
      <pre
        style={{
          whiteSpace: 'pre-wrap',
          fontFamily: 'var(--font-base, system-ui)',
          fontSize: '0.9rem',
          color: 'var(--text)',
          margin: 0,
        }}
      >
        {body}
      </pre>
    </CardContent>
  );
}
